import { type Carrier, type ReadRouterDeviceResponse, type RouterSimBody, type SimSlot, simSlot } from '@okvc/schema';

import { type RouterSim } from '../../models/index.js';
import { type RouterFormValues, type RouterSimFormValues } from '../Forms/formTypes.js';

type RouterSimFormFields = Pick<RouterFormValues, 'sims' | 'primarySlot'>;

export const SIM_SLOT_LABELS: Record<SimSlot, string> = {
  physical: 'Physical SIM',
  esim: 'eSIM',
};

export const CARRIER_LABELS: Record<Carrier, string> = {
  verizon: 'Verizon',
  simpl: 'Simpl (T-Mobile)',
  kajeet: 'Kajeet',
};

const EMPTY_SIM: RouterSimFormValues = { carrier: '', iccid: '', ipAddress: '', apn: '' };

const toSimFormValues = (sim?: RouterSim): RouterSimFormValues => {
  if (!sim) {
    return EMPTY_SIM;
  }
  return { carrier: sim.carrier, iccid: sim.iccid, ipAddress: sim.ipAddress, apn: sim.apn ?? '' };
};

export const simFormValuesFromRouter = (sims: RouterSim[] = []): RouterSimFormFields => {
  const simInSlot = (slot: SimSlot) => sims.find((sim) => sim.slot === slot);
  return {
    sims: { physical: toSimFormValues(simInSlot('physical')), esim: toSimFormValues(simInSlot('esim')) },
    primarySlot: sims.find((sim) => sim.isPrimary)?.slot ?? 'physical',
  };
};

export const simBodiesFromFormValues = ({ sims, primarySlot }: RouterSimFormFields): RouterSimBody[] =>
  simSlot.options.flatMap((slot) => {
    const { carrier, iccid, ipAddress, apn } = sims[slot];
    if (!carrier) {
      return [];
    }
    return [{
      slot,
      carrier,
      iccid,
      ipAddress,
      apn: apn === '' ? undefined : apn,
      isPrimary: slot === primarySlot,
    }];
  });

const OPERATOR_CARRIERS: Array<[RegExp, Carrier]> = [
  [/verizon/i, 'verizon'],
  [/t-?mobile|simpl/i, 'simpl'],
  [/kajeet/i, 'kajeet'],
];

export const carrierForOperator = (operator: string): Carrier | '' =>
  OPERATOR_CARRIERS.find(([pattern]) => pattern.test(operator))?.[1] ?? '';

const keepOrFill = (current: string, read: string): string => (current === '' ? read : current);

export const simFormValuesFromDevice = (
  current: RouterSimFormValues,
  activeSim: NonNullable<ReadRouterDeviceResponse['activeSim']>,
): RouterSimFormValues => ({
  carrier: current.carrier === '' ? carrierForOperator(activeSim.operator) : current.carrier,
  iccid: activeSim.iccid,
  ipAddress: keepOrFill(current.ipAddress, activeSim.ipAddress),
  apn: keepOrFill(current.apn, activeSim.apn),
});
